export const CONVERTER_API_BASE = (
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_CONVERTER_BASE_URL) ||
  'http://localhost:3011'
).replace(/\/+$/, '');

export type ConverterFsEntry = {
  name: string;
  path: string;
  isDir: boolean;
  size?: number | null;
  mtime?: string | null;
};

type ConverterFsListing = {
  root: string;
  entries: ConverterFsEntry[];
};

export type EmittedLayerType =
  | 'scene-package'
  | '3d-tiles'
  | 'terrain'
  | 'point-cloud'
  | 'pmtiles'
  | 'cog'
  | 'model'
  | 'geojson'
  | 'metadata'
  | 'unknown';

export type EmittedLayerRecord = {
  id: string;
  name: string;
  code: string;
  path: string;
  directory: string;
  layerType: EmittedLayerType;
  typeLabel: string;
  url: string;
  size: number | null;
  sizeLabel: string;
  mtime: string | null;
  dateLabel: string;
  crs: string;
  center: string;
  bbox: [number, number, number, number] | null;
};

const RENDERABLE_EXTENSIONS = ['.tif', '.tiff', '.cog', '.pmtiles', '.glb', '.gltf', '.copc.laz', '.geojson', '.json'] as const;
const RENDERABLE_FILES = ['scene-package.json', 'tileset.json', 'layer.json', 'metadata.json'] as const;

function cleanPath(path: string): string {
  return path.replace(/^\/data\/out\//, '').replace(/^\\data\\out\\/, '').replace(/\\/g, '/');
}

function parentDirectory(path: string): string {
  const cleaned = cleanPath(path);
  const idx = cleaned.lastIndexOf('/');
  return idx > -1 ? cleaned.slice(0, idx) : '__root__';
}

function isRenderable(entry: ConverterFsEntry): boolean {
  if (entry.isDir) return false;
  const lower = entry.name.toLowerCase();
  if (RENDERABLE_FILES.includes(lower as (typeof RENDERABLE_FILES)[number])) return true;
  return RENDERABLE_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

function typeFor(entry: ConverterFsEntry): { layerType: EmittedLayerType; typeLabel: string } {
  const lower = entry.name.toLowerCase();
  if (lower === 'scene-package.json') return { layerType: 'scene-package', typeLabel: 'Scene Package' };
  if (lower === 'tileset.json') return { layerType: '3d-tiles', typeLabel: '3D Tiles' };
  if (lower === 'layer.json') return { layerType: 'terrain', typeLabel: 'Terrain' };
  if (lower === 'metadata.json') return { layerType: 'point-cloud', typeLabel: 'Point Cloud / Potree' };
  if (lower.endsWith('.copc.laz') || lower.endsWith('.copc')) return { layerType: 'point-cloud', typeLabel: 'Point Cloud / COPC' };
  if (lower.endsWith('.pmtiles')) return { layerType: 'pmtiles', typeLabel: 'PMTiles' };
  if (lower.endsWith('.tif') || lower.endsWith('.tiff') || lower.endsWith('.cog')) return { layerType: 'cog', typeLabel: 'COG Raster' };
  if (lower.endsWith('.glb') || lower.endsWith('.gltf')) return { layerType: 'model', typeLabel: 'GLB/glTF' };
  if (lower.endsWith('.geojson')) return { layerType: 'geojson', typeLabel: 'GeoJSON' };
  if (lower.endsWith('.json')) return { layerType: 'metadata', typeLabel: 'JSON Metadata' };
  return { layerType: 'unknown', typeLabel: entry.name.split('.').pop()?.toUpperCase() ?? 'Unknown' };
}

export function formatLayerBytes(bytes: number | null | undefined): string {
  if (bytes == null) return 'Unknown size';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return 'No date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function outputUrl(path: string): string {
  const cleaned = cleanPath(path);
  return `${CONVERTER_API_BASE}/converter-output/${cleaned.split('/').map(encodeURIComponent).join('/')}`;
}

async function listConverterDir(path = ''): Promise<ConverterFsEntry[]> {
  const params = new URLSearchParams();
  if (path) params.set('path', path);
  params.set('limit', '500');
  const res = await fetch(`${CONVERTER_API_BASE}/api/v1/converter/fs/outputs?${params.toString()}`);
  if (!res.ok) throw new Error(`Converter output API ${res.status}: ${await res.text()}`);
  const body = (await res.json()) as ConverterFsListing;
  return body.entries ?? [];
}

export async function listEmittedLayers(): Promise<EmittedLayerRecord[]> {
  const rootEntries = await listConverterDir();
  const nestedResults = await Promise.all(
    rootEntries
      .filter((entry) => entry.isDir)
      .map(async (entry) => {
        try {
          return await listConverterDir(entry.path);
        } catch {
          return [] as ConverterFsEntry[];
        }
      }),
  );
  const files = [...rootEntries.filter((entry) => !entry.isDir), ...nestedResults.flat()]
    .filter(isRenderable);

  return files.map((entry) => {
    const cleaned = cleanPath(entry.path);
    const { layerType, typeLabel } = typeFor(entry);
    return {
      id: cleaned,
      name: parentDirectory(cleaned) === '__root__' ? entry.name : `${parentDirectory(cleaned)} / ${entry.name}`,
      code: cleaned,
      path: cleaned,
      directory: parentDirectory(cleaned),
      layerType,
      typeLabel,
      url: outputUrl(cleaned),
      size: entry.size ?? null,
      sizeLabel: formatLayerBytes(entry.size),
      mtime: entry.mtime ?? null,
      dateLabel: formatDate(entry.mtime),
      crs: 'Unknown CRS',
      center: 'No center',
      bbox: null,
    };
  }).sort((a, b) => {
    if (a.mtime && b.mtime) return b.mtime.localeCompare(a.mtime);
    if (a.mtime) return -1;
    if (b.mtime) return 1;
    return a.name.localeCompare(b.name);
  });
}
